import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useQuery } from '@tanstack/react-query';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import CommissionTable from '../components/CommissionTable';
import SubmitCommissionDialog from '../components/SubmitCommissionDialog';
import CommissionHistoryModal from '../components/CommissionHistoryModal';
import { getAllCompanies } from '../queries/companies';
import { getAllProducts } from '../queries/products';
import {
  getAllCommissions,
  useCreateCommission,
  useDeleteCommission,
  useSetCommissionPaid,
  useUnlockCommission,
  useUpdateCommission,
} from '../queries/commissions';
import { formatMoney, parseEuroNumber } from '../utils/helper';
import PageShell, { PageActionButton } from '../components/PageShell';
import { UserContext } from '../context/UserContext';
import { USER_ROLES } from '../constants';
import { getBillingDetail } from '../queries/billingDetail';
import { toast } from 'react-toastify';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const lightColor1 = '#f0f4ff';
const lightColor2 = '#f4f6f8';

const CommissionOverview = () => {
  const { user } = useContext(UserContext);

  const { data: companies } = useQuery(getAllCompanies({}));
  const { data: products } = useQuery(getAllProducts({}));
  const { data: commissions, isLoading, isRefetching, refetch } = useQuery(getAllCommissions({}));
  const { data: billingDetailData } = useQuery(getBillingDetail({}));

  const { mutate: createCommission, isPending: isCreatingCommission } = useCreateCommission();
  const { mutate: updateCommission, isPending: isUpdatingCommission } = useUpdateCommission();
  const { mutate: deleteCommission } = useDeleteCommission();
  const { mutate: unlockCommission, isPending: isUnlocking } = useUnlockCommission();
  const { mutate: setCommissionPaid, isPending: isUpdatingPaid } = useSetCommissionPaid();

  const [expandedYears, setExpandedYears] = useState([new Date().getFullYear()]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [invoiceNumbers, setInvoiceNumbers] = useState({});
  const [data, setData] = useState({});
  const [unlockDialog, setUnlockDialog] = useState({ open: false, row: null, reason: '' });
  const [unpaidDialog, setUnpaidDialog] = useState({ open: false, row: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, row: null });
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth();
  const currentMonth = months[currentMonthIndex];
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    if (!isLoading && !isRefetching) {
      const groupedData =
        commissions?.reduce((acc, item) => {
          let year = currentYear;
          let monthName = currentMonth;
          if (item.locked) {
            const bucketDate = new Date(item.approvedAt || item.submittedAt);
            const month = bucketDate.getUTCMonth();
            year = bucketDate.getUTCFullYear();
            monthName = months[month];
          }
          if (!acc[year]) {
            acc[year] = {};
          }
          if (!acc[year][monthName]) {
            acc[year][monthName] = [];
          }
          acc[year][monthName].push(item);

          return acc;
        }, {}) || {};
      setData(groupedData);
    }
  }, [isLoading, isRefetching, commissions, currentYear, currentMonth]);

  const toggleYear = (year) => {
    setExpandedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year].sort((a, b) => b - a),
    );
  };

  const onSubmitCommission = (formData) => {
    const payload = {
      client: formData.client,
      product: formData.product,
      prospect: formData.prospect,
      event: formData.event,
      otherEvent: formData.otherEvent || '',
      commission: formData.commission,
      cents: formData.cents || '0',
      notes: formData.notes || '',
    };
    if (editData?._id) {
      const { product, ...updatePayload } = payload;
      updateCommission(
        { id: editData._id, data: updatePayload },
        {
          onSuccess: () => {
            refetch();
            setSubmitConfirmOpen(false);
            setOpenDialog(false);
            toast('Updated commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            setSubmitConfirmOpen(false);
            toast(error?.response?.data?.message || error?.response?.data?.error || error.message, { type: 'error' });
          },
        },
      );
    } else {
      createCommission(
        { data: payload },
        {
          onSuccess: () => {
            refetch();
            setSubmitConfirmOpen(false);
            setOpenDialog(false);
            toast('Created commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            setSubmitConfirmOpen(false);
            toast(error?.response?.data?.message || error?.response?.data?.error || error.message, { type: 'error' });
          },
        },
      );
    }
  };

  const onDeleteCommission = (row) => {
    if (row?._id) {
      deleteCommission(
        { id: row._id },
        {
          onSuccess: () => {
            refetch();
            toast('Deleted commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to delete commission.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  const onUnlockCommission = () => {
    const reason = unlockDialog.reason.trim();
    if (!unlockDialog.row?._id || !reason) return;

    unlockCommission(
      { id: unlockDialog.row._id, data: { reason } },
      {
        onSuccess: () => {
          refetch();
          setUnlockDialog({ open: false, row: null, reason: '' });
          toast('Unlocked commission successfully!', { type: 'success' });
        },
        onError: (error) => {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to unlock commission.';
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const onTogglePaid = (row, checked) => {
    if (!row?._id) return;
    if (!checked) {
      setUnpaidDialog({ open: true, row });
      return;
    }

    setCommissionPaid(
      { id: row._id, data: { paid: true } },
      {
        onSuccess: () => {
          refetch();
          toast('Marked as paid successfully!', { type: 'success' });
        },
        onError: (error) => {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to update paid status.';
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const onConfirmUnpaid = () => {
    if (!unpaidDialog.row?._id) return;

    setCommissionPaid(
      { id: unpaidDialog.row._id, data: { paid: false } },
      {
        onSuccess: () => {
          refetch();
          setUnpaidDialog({ open: false, row: null });
          toast('Paid status unchecked successfully!', { type: 'success' });
        },
        onError: (error) => {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to update paid status.';
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const years = [currentYear, currentYear - 1];

  const totalGlobalCommission = Object.values(data)
    .flatMap((yearData) => Object.values(yearData).flat())
    .reduce((sum, entry) => {
      const int = parseEuroNumber(entry.commission);
      const dec = parseInt(entry.cents || '0', 10);
      return sum + int + dec / 100;
    }, 0);

  return (
    <>
      <PageShell
        title='Commissions'
        subtitle={isAdmin ? undefined : 'Log commissions and track approval and payment status.'}
        actions={
          <>
            <Typography variant='h6' color='text.secondary' fontWeight='normal'>
              {formatMoney(totalGlobalCommission)}
            </Typography>
            {!isAdmin && (
              <PageActionButton
                onClick={() => {
                  setOpenDialog(true);
                  setEditData(null);
                }}
              >
                Submit commission
              </PageActionButton>
            )}
          </>
        }
      >
        {years.map((year) => {
          const totalYearCommission = Object.values(data[year] || {})
            .flat()
            .reduce((sum, entry) => {
              const int = parseEuroNumber(entry.commission);
              const dec = parseInt(entry.cents || '0', 10);
              return sum + int + dec / 100;
            }, 0);

          return (
            <Box key={year} mb={5}>
              <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                <Box display='flex' alignItems='center' gap={1}>
                  <Typography variant='h6' fontWeight='bold'>
                    {year}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {formatMoney(totalYearCommission)}
                  </Typography>
                </Box>
                <IconButton onClick={() => toggleYear(year)}>
                  {expandedYears.includes(year) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedYears.includes(year)} timeout={300}>
                {[...months].reverse().map((month, idx) => {
                  const monthIndex = 11 - idx;
                  if (year === currentYear && monthIndex > currentMonthIndex) return null;
                  const bgColor = idx % 2 === 0 ? lightColor1 : lightColor2;
                  const monthData = data[year]?.[month] || [];
                  const monthTotal = monthData.reduce((sum, entry) => {
                    const int = parseEuroNumber(entry.commission);
                    const dec = parseInt(entry.cents || '0', 10);
                    return sum + int + dec / 100;
                  }, 0);

                  return (
                    <Box
                      key={month}
                      mt={2}
                      p={2}
                      sx={{
                        backgroundColor: bgColor,
                        borderRadius: 2,
                        mb: 3,
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                        <Box display='flex' alignItems='center' gap={1}>
                          <CalendarMonthIcon fontSize='small' />
                          <Typography variant='subtitle1' fontWeight='bold'>
                            {month}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {formatMoney(monthTotal)}
                          </Typography>
                        </Box>
                      </Box>

                      <CommissionTable
                        monthData={monthData}
                        showInvoiceFields={!(year === currentYear && month === currentMonth)}
                        isEditable={year === currentYear && month === currentMonth}
                        isAdmin={isAdmin}
                        onDelete={onDeleteCommission}
                        onEdit={(row) => {
                          setEditData(row);
                          setOpenDialog(true);
                        }}
                        onUnlock={(row) => {
                          setUnlockDialog({ open: true, row, reason: '' });
                        }}
                        onShowHistory={(row) => setHistoryDialog({ open: true, row })}
                        onTogglePaid={onTogglePaid}
                        invoiceNumber={invoiceNumbers[`${year}-${month}`] || ''}
                        setInvoiceNumber={(value) =>
                          setInvoiceNumbers((prev) => ({ ...prev, [`${year}-${month}`]: value }))
                        }
                        onDownloadInvoice={() => {
                          generateInvoicePDF({
                            invoiceNumber: invoiceNumbers[`${year}-${month}`] || '',
                            month,
                            year,
                            commissions: monthData,
                            isBatch: false,
                            agentDetails: { ...billingDetailData, ...user },
                          });
                        }}
                      />
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </PageShell>

      <SubmitCommissionDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        submitConfirmOpen={submitConfirmOpen}
        setSubmitConfirmOpen={setSubmitConfirmOpen}
        onSubmitCommission={onSubmitCommission}
        companies={companies}
        products={products || []}
        currentMonth={currentMonth}
        currentYear={currentYear}
        defaultValues={editData || {}}
        isSubmitting={isCreatingCommission || isUpdatingCommission}
      />

      <CommissionHistoryModal
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, row: null })}
        commission={historyDialog.row}
        userRole={user?.role}
      />

      <Dialog
        open={unlockDialog.open}
        onClose={() => setUnlockDialog({ open: false, row: null, reason: '' })}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Unlock Commission</DialogTitle>
        <DialogContent>
          <Typography variant='body2' mb={2}>
            Reason for unlocking is required.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label='Reason for unlocking'
            value={unlockDialog.reason}
            onChange={(e) => setUnlockDialog((prev) => ({ ...prev, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, row: null, reason: '' })} color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={onUnlockCommission}
            variant='contained'
            color='primary'
            disabled={isUnlocking || !unlockDialog.reason.trim()}
          >
            Unlock
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unpaidDialog.open} onClose={() => setUnpaidDialog({ open: false, row: null })}>
        <DialogTitle>Remove paid status</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>Are you sure you want to remove the paid status?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpaidDialog({ open: false, row: null })} color='secondary'>
            Cancel
          </Button>
          <Button onClick={onConfirmUnpaid} variant='contained' color='primary' disabled={isUpdatingPaid}>
            Remove paid status
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommissionOverview;
